import { AccountInfo, Configuration, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';
import { AzureConfig } from '@/shared/constants/AzureConfig';
import { present } from '@/shared/utils/typescript';

// DOCS: https://learn.microsoft.com/en-us/azure/active-directory/develop/scenario-spa-overview

export type AzureAccessToken = string;

export class AuthenticationPending extends Error {}

const CONFIG: Configuration = {
  auth: {
    clientId: AzureConfig.AZURE_AD_CLIENT_ID,
    authority: AzureConfig.AZURE_AD_AUTHORITY,
    redirectUri: `${location.protocol}//${location.host}`, // eslint-disable-line no-restricted-globals
    navigateToLoginRequestUrl: false,
  },
};

const SCOPES = [AzureConfig.AZURE_AD_SCOPE];

class AzureAuthService {
  private client = new PublicClientApplication(CONFIG);
  private account: AccountInfo | null = null;

  async login(): Promise<AzureAccessToken> {
    const accounts = this.client.getAllAccounts();
    if (accounts.length === 0) {
      await this.requestLogin();
      throw new AuthenticationPending();
    } else {
      if (accounts.length > 1) console.warn('multiple accounts signed in - choosing first one'); // eslint-disable-line no-console
      this.account = accounts[0];
      return this.requestAccessToken();
    }
  }

  async handleRedirect(): Promise<AzureAccessToken | null> {
    const redirectResponse = await this.client.handleRedirectPromise();
    if (!redirectResponse) return null;
    this.account = redirectResponse.account;
    return redirectResponse.accessToken;
  }

  async logout(): Promise<void> {
    if (this.account) {
      const request = { scopes: SCOPES, account: this.account };
      this.account = null;
      await this.client.logoutRedirect(request);
    }
  }

  private async requestLogin(): Promise<void> {
    const request = { scopes: SCOPES, prompt: 'select_account' };
    await this.client.loginRedirect(request);
  }

  private async requestAccessToken(): Promise<AzureAccessToken> {
    const request = { scopes: SCOPES, account: present(this.account) };
    try {
      const result = await this.client.acquireTokenSilent(request);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await this.client.acquireTokenRedirect(request);
        throw new AuthenticationPending();
      } else {
        throw error;
      }
    }
  }
}

export default new AzureAuthService();
